
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Edit, Save, X, RefreshCw } from "lucide-react";
import { StaticContent } from "./useStaticText";

interface StaticContentItemProps {
  content: StaticContent;
  editingId: string | null;
  editedContent: string;
  setEditedContent: (content: string) => void;
  isSaving: boolean;
  handleEdit: (content: StaticContent) => void;
  handleCancelEdit: () => void;
  handleSave: (id: string) => void;
}

const StaticContentItem: React.FC<StaticContentItemProps> = ({
  content,
  editingId,
  editedContent,
  setEditedContent,
  isSaving,
  handleEdit,
  handleCancelEdit,
  handleSave
}) => {
  const isEditing = content.id === editingId;

  return (
    <Card key={content.id} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
          {/* Info section */}
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Página:</span>
              <span className="ml-2 inline-block bg-gray-100 text-gray-800 text-sm px-2 py-0.5 rounded">
                {content.page}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Seção:</span>
              <span className="ml-2 inline-block bg-gray-100 text-gray-800 text-sm px-2 py-0.5 rounded">
                {content.section}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Chave:</span>
              <span className="ml-2 inline-block bg-blue-50 text-blue-800 text-sm px-2 py-0.5 rounded">
                {content.key}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              Última atualização: {content.updated_at ? new Date(content.updated_at).toLocaleString('pt-BR') : 'N/A'}
            </div>

            {!isEditing && (
              <div className="mt-4 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(content)}
                  className="w-full"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </div>
            )}
          </div>
          
          {/* Content section */}
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={6}
                className="min-h-[100px]"
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="bg-toctoc-blue hover:bg-blue-700"
                  onClick={() => handleSave(content.id)}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="bg-gray-50 p-4 rounded-md text-sm whitespace-pre-wrap">
                {content.content}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StaticContentItem;
